import React from "react"
import {
	Assignee,
	AssignmentActionRequestType,
	AssignmentStage,
	AssignmentStatus,
	GeneralReviewApproveRequest,
	GeneralReviewAssignmentItemPayload,
	GeneralReviewEscalateRequest,
	GeneralReviewRejectReason,
	GeneralReviewRejectRequest,
	update,
} from "../../resources/assignments"
import {Customer} from "../../resources/customer"
import {useResetScrollTop} from "../../hooks/useResetScrollTop"
import {
	AssignmentPageItem,
	AssignmentRiskFlag,
	AssignmentRiskFlagSeverity,
} from "../AssignmentPageItem/AssignmentPageItem"
import {AssignmentPageItemApproveRejectFooter} from "../AssignmentPageItemApproveRejectFooter/AssignmentPageItemApproveRejectFooter"
import {DetailsList} from "../DetailsList/DetailsList"
import {Claims, useUserClaimsData} from "../../services/auth"
import {useModal} from "react-modal-hook"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {
	AssignmentApproveRejectModal,
	AssignmentStatusUpdateReasonsMapping,
} from "../AssignmentApproveRejectModal/AssignmentApproveRejectModal"
import {AssignmentPageCompletedItemHeader} from "../AssignmentPageCompletedItemHeader/AssignmentPageCompletedItemHeader"
import {Account} from "../../resources/account"
import {CheckDeposit} from "../../resources/checkDeposits"
import AssignmentCheckDepositDetails from "../AssignmentCheckDepositDetails/AssignmentCheckDepositDetails"
import {AssignmentEscalateModal} from "../AssignmentEscalateModal/AssignmentEscalateModal"
import UpdateCheckDepositModal from "../CheckDeposits/UpdateCheckDepositModal"

interface AssignmentPageItemGeneralReviewCheckDepositProps {
	id: string
	assignees: Assignee[]
	status: AssignmentStatus
	stage: AssignmentStage
	title: string
	description?: string | JSX.Element
	createdAt: Date
	statusUpdatedAt?: Date
	isOwnersItem?: boolean
	checkDeposit?: CheckDeposit
	account?: Account
	customer?: Customer
	payload: GeneralReviewAssignmentItemPayload
	accessToken: string
	refresh: () => void
	refreshToken: number
	disabled?: boolean
	claims?: Claims
	frontImage?: ArrayBuffer | null | undefined
	backImage?: ArrayBuffer | null | undefined
}

export enum GeneralReviewCheckDepositStatusUpdateReasonKeys {
	BlankCheck = "Blank check",
	MissingDate = "Missing date",
	PostDated = "Post dated",
	StaleDated = "Stale dated",
	MissingSignature = "Missing signature",
	MissingEndorsement = "Missing endorsement",
	AmountMismatchMDC = "Amount mismatch - MDC doesn't match check",
	AmountMismatchLegalText = "Amount mismatch - Legal text doesn't match numeric amount",
	DuplicatedCheck = "Duplicated check",
	PartialEndorsement = "Partial endorsement",
	NameMismatchBeneficiary = "Name mismatch - Beneficiary",
	IneligibleItemScreenCapturedImage = "Ineligible item - Screen Captured Image",
	IneligibleItemFoldedOrTornCorners = "Ineligible item - Folded or Torn Corners",
	AlteredFictitiousItem = "Altered/Fictitious item",
}

const generalReviewCheckDepositAssignmentStatusUpdateReasons: AssignmentStatusUpdateReasonsMapping = {
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.BlankCheck]: {
		reason: "BlankCheck",
		displayName: "Blank Check",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.MissingDate]: {
		reason: "MissingDate",
		displayName: "Missing Date",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.PostDated]: {
		reason: "PostDated",
		displayName: "Post Dated",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.StaleDated]: {
		reason: "StaleDated",
		displayName: "Stale Dated",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.MissingSignature]: {
		reason: "MissingSignature",
		displayName: "Missing Signature",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.MissingEndorsement]: {
		reason: "MissingEndorsement",
		displayName: "Missing Endorsement",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.AmountMismatchMDC]: {
		reason: "AmountMismatchMDC",
		displayName: "Amount Mismatch - MDC doesn't match check",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.AmountMismatchLegalText]: {
		reason: "AmountMismatchLegalText",
		displayName: "Amount Mismatch - Legal text doesn't match numeric amount",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.DuplicatedCheck]: {
		reason: "DuplicatedCheck",
		displayName: "Duplicated Check",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.PartialEndorsement]: {
		reason: "PartialEndorsement",
		displayName: "Partial Endorsement",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.NameMismatchBeneficiary]: {
		reason: "NameMismatchBeneficiary",
		displayName: "Name Mismatch - Beneficiary",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.IneligibleItemScreenCapturedImage]: {
		reason: "IneligibleItemScreenCapturedImage",
		displayName: "Ineligible Item - Screen Captured Image",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.IneligibleItemFoldedOrTornCorners]: {
		reason: "IneligibleItemFoldedOrTornCorners",
		displayName: "Ineligible Item - Folded or Torn Corners",
	},
	[GeneralReviewCheckDepositStatusUpdateReasonKeys.AlteredFictitiousItem]: {
		reason: "AlteredFictitiousItem",
		displayName: "Altered/Fictitious Item",
	},
}

export function AssignmentPageItemGeneralReviewCheckDeposit({
	id,
	assignees,
	status,
	stage,
	title,
	description,
	createdAt,
	statusUpdatedAt,
	isOwnersItem,
	checkDeposit,
	account,
	customer,
	payload: {reason, reasonText, isEscalated},
	accessToken,
	refresh,
	refreshToken,
	disabled,
	claims,
	frontImage,
	backImage,
}: AssignmentPageItemGeneralReviewCheckDepositProps) {
	const checkDepositDetailsContentRef = React.useRef<HTMLDivElement>(null)
	useResetScrollTop(checkDepositDetailsContentRef, [id])
	const userClaims = useUserClaimsData(claims)

	const [showEscalateModal, hideEscalateModal] = useModal(() => {
		const successText = "Escalated successfully"
		const [state, callback] = useAsyncResultIdle(update)
		const onSubmit = ({reasonText}: {reasonText: string}) => {
			const data: GeneralReviewEscalateRequest = {
				type: AssignmentActionRequestType.generalReviewEscalateRequest,
				attributes: {
					reasonText,
				},
			}
			return callback({accessToken, id, data})
		}
		return (
			<AssignmentEscalateModal
				title="Are you sure you want to escalate?"
				state={state}
				onSubmit={onSubmit}
				withReasonText
				primaryButtonText="Escalate Item"
				primaryButtonClassname="is-outlined"
				closeModal={hideEscalateModal}
				onSuccess={refresh}
				successText={successText}
				successClassname="has-text-black"
			/>
		)
	}, [id])

	const [showApprove, hideApprove] = useModal(() => {
		const successText = "Approved successfully"
		const [state, callback] = useAsyncResultIdle(update)
		const onSubmit = (args: {reasonText: string} | null) => {
			const data: GeneralReviewApproveRequest = {
				type: AssignmentActionRequestType.generalReviewApproveRequest,
				attributes: {
					reasonText: args?.reasonText,
				},
			}
			return callback({accessToken, id, data})
		}
		return (
			<AssignmentApproveRejectModal
				title="Approve Item"
				description={
					<p>
						Are you sure you want to approve <span className="has-text-weight-bold">{`General Review #${id}?`}</span>
					</p>
				}
				state={state}
				onSubmit={onSubmit}
				withReasonText
				isOptionalReasonText
				reasonTextPlaceholder="Please provide a comment if there were initial concerns cleared by additional outreach or investigation."
				primaryButtonText="Approve"
				primaryButtonClassname="is-success"
				closeModal={hideApprove}
				onSuccess={refresh}
				successText={successText}
			/>
		)
	}, [id])

	const [showReject, hideReject] = useModal(() => {
		const successText = "Rejected successfully"
		const [state, callback] = useAsyncResultIdle(update)
		const onSubmit = ({reason, reasonText}: {reason: GeneralReviewRejectReason; reasonText?: string}) => {
			const data: GeneralReviewRejectRequest = {
				type: AssignmentActionRequestType.generalReviewRejectRequest,
				attributes: {
					reason,
					...(reason === GeneralReviewRejectReason.Other ? {reasonText} : {}),
				},
			}
			return callback({accessToken, id, data})
		}
		return (
			<AssignmentApproveRejectModal
				title="Reject Item"
				state={state}
				onSubmit={onSubmit}
				withReasonText
				reasonTextPlaceholder="Why are you rejecting this based on general review?"
				reasons={generalReviewCheckDepositAssignmentStatusUpdateReasons}
				reasonsToEnableReasonText={[]}
				primaryButtonText="Reject"
				primaryButtonClassname="is-danger"
				closeModal={hideReject}
				onSuccess={refresh}
				successText={successText}
				successClassname="has-text-black"
			/>
		)
	}, [id])

	const assignmentFlags: AssignmentRiskFlag[] =
		checkDeposit?.attributes.riskFlags?.map((rf) => ({
			name: rf.name,
			severity: rf.severity as AssignmentRiskFlagSeverity,
		})) ?? []

	const [showUpdateModal, hideUpdateModal] = useModal(() =>
		checkDeposit?.id ? (
			<UpdateCheckDepositModal
				checkDepositId={checkDeposit.id}
				close={hideUpdateModal}
				refreshFunc={refresh}
				refreshToken={refreshToken}
			/>
		) : (
			<></>
		)
	)

	return (
		<AssignmentPageItem
			assignees={assignees}
			status={status}
			stage={stage}
			title={title}
			description={description}
			createdAt={createdAt}
			statusUpdatedAt={statusUpdatedAt}
			isOwnersItem={isOwnersItem}
			disabled={disabled}
			assignmentFlags={assignmentFlags}
			escalatedReason={isEscalated && reasonText ? reasonText : undefined}
			pageItemFooter={
				!disabled ? (
					<AssignmentPageItemApproveRejectFooter
						approveLabel="Approve"
						rejectLabel="Reject"
						onApprove={showApprove}
						onReject={showReject}
						onEscalate={showEscalateModal}
						escalateLabel={isEscalated ? undefined : "Escalate"}
					/>
				) : undefined
			}
			pageCompletedItemHeader={
				<AssignmentPageCompletedItemHeader
					assignees={assignees}
					status={status}
					reason={reason}
					reasonText={reasonText}
				/>
			}
		>
			<DetailsList
				title="Check Deposit Summary"
				icon="payment-box-incoming"
				className="scrollable-container"
				contentRef={checkDepositDetailsContentRef}
				disabled={disabled}
			>
				{checkDeposit && (
					<div className="general-review-item-details">
						<AssignmentCheckDepositDetails
							checkDeposit={checkDeposit}
							account={account}
							customer={customer}
							disabled={disabled}
							accessToken={accessToken}
							claims={userClaims}
							className="modified-padding"
							frontImage={frontImage}
							backImage={backImage}
							showUpdateModal={showUpdateModal}
						/>
					</div>
				)}
			</DetailsList>
		</AssignmentPageItem>
	)
}
